import {useWallet} from "@topo/wallet-adapter-react";
import {Avatar, Button, Typography} from "@mui/material";
import React, {JSX, useState} from "react";
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiWallet3Line,
} from "@remixicon/react";
import WalletMenu from "./WalletMenu";
import IdenticonImg from "./IdenticonImg";
import {standardizeAddress} from "../utils";
import {truncateAddress} from "../pages/utils";
import useHoverDropdown from "./hooks/useHoverDropdown";

type WalletButtonProps = {
  handleModalOpen: () => void;
  handleNavigate?: () => void;
};

export default function WalletButton({
  handleModalOpen,
  handleNavigate,
}: WalletButtonProps): JSX.Element {
  const {connected, account, wallet} = useWallet();
  const dropdown = useHoverDropdown();

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(event.currentTarget);
    dropdown.show();
  };

  const handlePopoverClose = () => {
    dropdown.close();
  };

  const onConnectWalletClick = () => {
    handlePopoverClose();
    handleModalOpen();
  };

  const accountAddress = account?.address
    ? standardizeAddress(account.address.toString())
    : "";
  const displayAddress =
    account?.ansName ||
    (accountAddress ? truncateAddress(accountAddress) : "") ||
    "Unknown";
  const popoverOpen = connected && dropdown.open;

  return (
    <>
      <Button
        size="small"
        variant={connected ? "outlined" : "contained"}
        onClick={connected ? handleClick : onConnectWalletClick}
        onMouseEnter={(event) => {
          if (connected) {
            setPopoverAnchor(event.currentTarget);
            dropdown.triggerProps.onMouseEnter();
          }
        }}
        onMouseLeave={dropdown.triggerProps.onMouseLeave}
        aria-haspopup={connected ? "menu" : undefined}
        aria-expanded={connected ? popoverOpen : undefined}
        aria-controls={popoverOpen ? "wallet-menu" : undefined}
        className="wallet-button"
        sx={(theme) => ({
          borderRadius: "8px",
          height: 40,
          px: connected ? 1.5 : 2,
          py: 0,
          textTransform: "none",
          fontWeight: 500,
          fontSize: 14,
          ...(connected
            ? {
                color: theme.palette.tokens.text.primary,
                backgroundColor: theme.palette.tokens.layer.card,
                border: `1px solid ${
                  popoverOpen
                    ? theme.palette.tokens.line.input
                    : theme.palette.tokens.line.subtle
                }`,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  backgroundColor: theme.palette.tokens.layer.card,
                  border: `1px solid ${
                    popoverOpen
                      ? theme.palette.tokens.line.input
                      : theme.palette.tokens.line.strong
                  }`,
                },
                "&:active, &[aria-expanded=true]": {
                  border: `1px solid ${theme.palette.tokens.line.input}`,
                },
              }
            : {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.tokens.function.primary,
                "&:hover": {
                  backgroundColor: theme.palette.tokens.function.primaryClick,
                },
              }),
        })}
      >
        {connected ? (
          <>
            {accountAddress ? (
              <IdenticonImg address={accountAddress} size={20} />
            ) : (
              <Avatar
                alt={wallet?.name}
                src={wallet?.icon}
                sx={{width: 20, height: 20}}
              />
            )}
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "inherit",
                lineHeight: 1.4,
              }}
            >
              {displayAddress}
            </Typography>
            {popoverOpen ? (
              <RiArrowUpSLine size={18} style={{opacity: 0.7}} />
            ) : (
              <RiArrowDownSLine size={18} style={{opacity: 0.7}} />
            )}
          </>
        ) : (
          <>
            <RiWallet3Line size={18} style={{marginRight: 8}} />
            <Typography noWrap sx={{fontSize: 14, fontWeight: 500}}>
              Connect Wallet
            </Typography>
          </>
        )}
      </Button>
      <WalletMenu
        popoverAnchor={popoverAnchor}
        open={popoverOpen}
        openedByHover={dropdown.openedByHover}
        paperHoverProps={dropdown.paperProps}
        handlePopoverClose={handlePopoverClose}
        handleNavigate={handleNavigate}
      />
    </>
  );
}
